import { useMemo, useState } from "react";
import { X } from "lucide-react";
import { ErrorState } from "procurement/design";
import {
  useGetBudgetLineRowsQuery,
  useUpdateBudgetLineMutation,
  useCreateBudgetLineMutation,
  useUpdateBudgetAllocationPolicyMutation,
} from "shared/redux/endpoints/procurementV2";
import en from "procurement/design/i18n/en";
import { showToastSuccess, showToastError } from "utilities/toastUtil";
import PrettySwitch from "../PrettySwitch";
import BudgetLineEditableTable from "./BudgetLineEditableTable";
import { Button } from "../../ui";
import { getApiErrorMessage } from "../../orderSubmitFeedback";
import { formatVND } from "../../constants";

// All 12 months of one (category, center) pair. accumulateUnspent is a
// pair-level policy on the BE, so it gets one switch in the header instead of
// one per month row.
const BudgetPairModal = ({ pair, fiscalYear, canEdit, onClose }) => {
  const {
    data: rows = [],
    isFetching,
    isError,
    error,
    refetch,
  } = useGetBudgetLineRowsQuery(
    { categoryId: pair?.categoryId, centerId: pair?.centerId, fiscalYear },
    { skip: !pair }
  );
  const [updateBudgetLine, { isLoading: isSaving }] =
    useUpdateBudgetLineMutation();
  const [createBudgetLine, { isLoading: isToppingUp }] = useCreateBudgetLineMutation();
  const [updateAllocationPolicy, { isLoading: isUpdatingPolicy }] =
    useUpdateBudgetAllocationPolicyMutation();
  const [pendingAccumulate, setPendingAccumulate] = useState(null);

  const sorted = useMemo(
    () => [...rows].sort((a, b) => (a.fiscalMonth || 0) - (b.fiscalMonth || 0)),
    [rows]
  );
  const accumulate = sorted[0]?.accumulateUnspent !== false;

  const handleSaveLine = async (row, total, note) => {
    try {
      await updateBudgetLine({ id: row.id, total, note }).unwrap();
      showToastSuccess(en.toast.budgetUpdated);
      return true;
    } catch (e) {
      showToastError(
        getApiErrorMessage(
          e,
          "Couldn't update this budget line. Please try again."
        )
      );
      return false;
    }
  };
  // POST adds onto the existing month row; accumulateUnspent is left out so
  // the pair's policy doesn't change.
  const handleTopUpLine = async (row, amount, note) => {
    try {
      const updated = await createBudgetLine({
        categoryId: row.categoryId,
        centerId: row.centerId,
        fiscalYear: row.fiscalYear,
        fiscalMonth: row.fiscalMonth,
        total: amount,
        note: note || undefined,
      }).unwrap();
      showToastSuccess(
        `Added ${formatVND(amount)} to ${String(row.fiscalMonth).padStart(2, "0")}/${row.fiscalYear} — new total ${formatVND(updated?.totalAmount ?? row.total + amount)}.`
      );
      return true;
    } catch (e) {
      showToastError(getApiErrorMessage(e, "Couldn't add to this budget line. Please try again."));
      return false;
    }
  };
  const confirmAccumulate = async () => {
    try {
      await updateAllocationPolicy({
        id: sorted[0].id,
        accumulateUnspent: pendingAccumulate,
      }).unwrap();
      showToastSuccess(en.toast.budgetUpdated);
    } catch (e) {
      showToastError(
        getApiErrorMessage(
          e,
          "Couldn't update the accumulate policy. Please try again."
        )
      );
    }
    setPendingAccumulate(null);
  };

  if (!pair) return null;

  return (
    <div
      className="pc-modal-overlay"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="pc-modal"
        style={{
          maxWidth: "none",
          width: "90vw",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          className="pc-modal-head"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            paddingBottom: 12,
          }}
        >
          <div>
            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
              {pair.centerName} · {fiscalYear}
            </div>
            <div className="pc-modal-title">{pair.categoryName}</div>
          </div>
          <button
            type="button"
            className="pc-btn pc-btn--ghost pc-btn--sm"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={16} aria-hidden />
          </button>
        </div>
        <div
          className="pc-modal-body"
          style={{ overflow: "auto", paddingBottom: 20 }}
        >
          {sorted.length > 0 && (
            <div
              className="ds-attention"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginBottom: 16,
              }}
            >
              <PrettySwitch
                checked={accumulate}
                disabled={!canEdit || isUpdatingPolicy}
                onChange={(e) => setPendingAccumulate(e.target.checked)}
              />
              <div>
                <div style={{ fontWeight: 600 }}>Accumulate unspent budget</div>
                <div className="pc-hint">
                  {accumulate
                    ? "Unspent budget of each month rolls forward to the following months."
                    : "Each month only uses its own budget — leftovers don't roll forward."}
                </div>
              </div>
            </div>
          )}

          {isError ? (
            <ErrorState
              text={getApiErrorMessage(error, en.empty.loadErrorText)}
              action={
                <Button variant="secondary" size="sm" onClick={refetch}>
                  {en.common.retry}
                </Button>
              }
            />
          ) : (
            <BudgetLineEditableTable
              rows={sorted}
              isLoading={isFetching}
              isSaving={isSaving || isToppingUp}
              canEdit={canEdit}
              onSaveLine={handleSaveLine}
              onTopUpLine={handleTopUpLine}
            />
          )}

          {pendingAccumulate !== null && (
            <div
              className="pc-modal-overlay"
              role="dialog"
              aria-modal="true"
              onClick={(e) => {
                if (e.target === e.currentTarget && !isUpdatingPolicy)
                  setPendingAccumulate(null);
              }}
            >
              <div className="pc-modal" style={{ maxWidth: 440 }}>
                <div className="pc-modal-head">
                  <div className="pc-modal-title">
                    Turn {pendingAccumulate ? "ON" : "OFF"} accumulate for{" "}
                    {pair?.categoryName}?
                  </div>
                </div>
                <div className="pc-modal-body">
                  {pendingAccumulate
                    ? "Unspent budget will roll forward for every month of this category + center."
                    : "Every month of this category + center will stop receiving last month's leftover."}
                </div>
                <div className="pc-modal-foot">
                  <Button
                    variant="secondary"
                    onClick={() => setPendingAccumulate(null)}
                    disabled={isUpdatingPolicy}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={confirmAccumulate}
                    disabled={isUpdatingPolicy}
                  >
                    {isUpdatingPolicy ? "Saving…" : "Confirm"}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BudgetPairModal;
